import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";
import { ALREADY_REVIEWED, FINDING_DISCLAIMER, SESSION_NOTE } from "../copy/decisionReviewCopy";
import { CREATE_TASK_LABEL, DRAFT_TASK_NOTE, FINDING_IS_UNCONFIRMED } from "../copy/journeyCopy";
import { abbreviateSha, decisionSummary, verdictLabel } from "./reviewDisplay";
import DecisionReviewFinding from "./DecisionReviewFinding";
import DecisionReviewProgress from "./DecisionReviewProgress";
import ReviewPublicationStatus from "./ReviewPublicationStatus";
import CorrectionPlanPreview from "./CorrectionPlanPreview";
import CopyableSha from "../ui/CopyableSha";
import StatusBadge from "../ui/StatusBadge";
import InlineAlert from "../ui/InlineAlert";

export default function DecisionReviewDetail({
  review: initialReview = null,
  reviewId = null,
  mutateAllowed = false,
  alreadyReviewed = false,
  embedded = false,
  onUpdated,
  onTaskCreated,
}) {
  const [review, setReview] = useState(initialReview);
  const [capability, setCapability] = useState(initialReview?.publicationCapability || null);
  const [currentHead, setCurrentHead] = useState(null);
  const [currentId, setCurrentId] = useState(null);
  const [notes, setNotes] = useState({});
  const [draft, setDraft] = useState(null);
  const [sessionNote, setSessionNote] = useState("");
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({});
  const [outcome, setOutcome] = useState("");
  const [relatedTasks, setRelatedTasks] = useState([]);
  const [confirmStale, setConfirmStale] = useState(false);
  const [brainNote, setBrainNote] = useState("");
  const experimental = process.env.REACT_APP_EXPERIMENTAL_TASKS === "true";

  useEffect(() => {
    setReview(initialReview);
    setCapability(initialReview?.publicationCapability || null);
  }, [initialReview]);

  useEffect(() => {
    if (initialReview || !reviewId) return undefined;
    let cancelled = false;
    api
      .get(`/decision-reviews/${reviewId}`)
      .then((response) => {
        if (cancelled) return;
        setReview(response.data?.review || null);
        setCapability(response.data?.review?.publicationCapability || null);
      })
      .catch(() => {
        if (!cancelled) setError("That review is not available in this workspace.");
      });
    return () => {
      cancelled = true;
    };
  }, [initialReview, reviewId]);

  useEffect(() => {
    if (!review || (review.status !== "analyzing" && review.status !== "queued")) return undefined;
    const timer = setInterval(() => {
      api.get(`/decision-reviews/${review.id}`).then((response) => {
        const next = response.data?.review;
        if (!next) return;
        setReview(next);
        setCapability(next.publicationCapability || null);
        onUpdated?.(next);
      }).catch(() => {});
    }, 2000);
    return () => clearInterval(timer);
  }, [review, onUpdated]);

  useEffect(() => {
    if (!review || review.currentState !== "stale" || !review.repoId) return undefined;
    let cancelled = false;
    Promise.resolve(
      api.get(`/repos/${review.repoId}/decision-reviews`, { params: { pullNumber: review.pullNumber } })
    )
      .then((response) => {
        if (cancelled || !response?.data) return;
        const current = (response.data.current || [])[0];
        if (current?.headSha && current.headSha !== review.headSha) {
          setCurrentHead(current.headSha);
          setCurrentId(current.id);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [review]);

  useEffect(() => {
    if (!review?.id) return undefined;
    let cancelled = false;
    Promise.resolve(api.get(`/decision-reviews/${review.id}/task-drafts`))
      .then((response) => {
        if (!cancelled) setRelatedTasks(response?.data?.tasks || []);
      })
      .catch(() => {
        if (!cancelled) setRelatedTasks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [review?.id]);

  async function retry() {
    const response = await api.post(`/decision-reviews/${review.id}/publication`);
    const next = response.data?.review;
    if (next) {
      setReview(next);
      onUpdated?.(next);
    }
  }

  async function createTask() {
    const findingIds = Object.entries(selected).filter(([, on]) => on).map(([id]) => id);
    const response = await api.post(`/decision-reviews/${review.id}/task-drafts`, {
      findingIds,
      desiredOutcome: outcome,
      confirmStale: stale ? confirmStale : false,
    });
    const task = response.data?.task;
    if (task) {
      setRelatedTasks((current) => [task, ...current]);
      onTaskCreated?.(task);
    }
  }

  async function reauthorize() {
    const response = await api.get("/github-app/install-url");
    const url = response?.data?.installUrl;
    if (url && url.startsWith("https://github.com/apps/")) window.location.assign(url);
  }

  if (!review && error) return <InlineAlert tone="alert">{error}</InlineAlert>;
  if (!review) return <DecisionReviewProgress />;
  const Heading = embedded ? "h2" : "h1";
  const stale = review.currentState === "stale" || review.status === "superseded";
  const running = review.status === "analyzing" || review.status === "queued";

  return (
    <article className="rr-review rr-decision" data-testid="decision-review-current">
      <header className="rr-review-head">
        <p className="rr-kicker">Decision Review</p>
        <Heading>{review.title || `Pull request #${review.pullNumber}`}</Heading>
        <div className="rr-meta">
          <StatusBadge tone={review.status === "failed" ? "bad" : "good"}>
            {review.status === "failed" ? "Review failed" : verdictLabel(review.verdict)}
          </StatusBadge>
          <span data-testid="decision-review-verdict" className="rr-sr">
            {review.verdict || review.status}
          </span>
          <StatusBadge tone={stale ? "stale" : "good"}>
            <span className="rr-sr">{stale ? "This review is stale." : "This review is current."}</span>
            {stale ? "Stale" : "Current"}
          </StatusBadge>
          <CopyableSha sha={review.headSha} />
        </div>
        <p className="rr-lead">
          {review.owner}/{review.name} #{review.pullNumber}
          {(review.findings || []).length ? ` · ${(review.findings || []).length} findings` : ""}
          {review.contextSnapshot?.sufficient ? " · Product context was available." : ` · ${review.contextSnapshot?.warning || "Product context is limited."}`}
        </p>
      </header>
      <div className="rr-review-main">
      {alreadyReviewed ? <InlineAlert>{ALREADY_REVIEWED}</InlineAlert> : null}
      {stale ? (
        <InlineAlert testId="stale-review-banner">
          <p>
            This review covers <span className="rr-sha">{abbreviateSha(review.headSha)}</span>.
            {currentHead ? (
              <>
                {" "}The pull request is now at <span className="rr-sha">{abbreviateSha(currentHead)}</span>.
              </>
            ) : (
              " The pull request has a newer commit."
            )}
          </p>
          {currentId ? <a href={`/reviews/${currentId}`}>Open the current review</a> : null}
        </InlineAlert>
      ) : null}
      {running ? <DecisionReviewProgress publishing={Boolean(capability?.possible)} /> : null}
      {review.status === "failed" ? (
        <p data-testid="decision-review-status" role="alert">
          The review failed. {review.failureMessage || error}
        </p>
      ) : (
        <p data-testid="decision-review-status">{decisionSummary(review)}</p>
      )}
      <p className="rr-muted">{FINDING_IS_UNCONFIRMED} {FINDING_DISCLAIMER}</p>
      {sessionNote ? <p>{SESSION_NOTE}</p> : null}
      {brainNote ? <p>{brainNote}</p> : null}
      {review.billing?.state === "reserved" ? <p>1 Review Credit reserved. It will be returned if the review cannot finish.</p> : null}
      {review.billing?.state === "consumed" ? <p>1 Review Credit used.</p> : null}
      {review.billing?.state === "released" ? <p>The review could not finish. Your credit was returned.</p> : null}
      {review.billing?.state === "consumed" && review.publication?.state === "failed" ? <p>The review completed and used 1 credit, but GitHub publication needs attention.</p> : null}
      {(review.findings || []).slice(0, 5).map((finding) => (
        <div key={finding.findingId}>
          {experimental && mutateAllowed && !running ? (
            <label>
              <input
                type="checkbox"
                aria-label={`Select ${finding.title}`}
                checked={Boolean(selected[finding.findingId])}
                onChange={(event) => setSelected((current) => ({ ...current, [finding.findingId]: event.target.checked }))}
              />
              Include in a Task
            </label>
          ) : null}
          <DecisionReviewFinding
            finding={finding}
            disposition={notes[finding.findingId]}
            stale={stale}
            onFeedback={async (_finding, payload) => {
              const response = await api.post(`/decision-reviews/${review.id}/findings/${_finding.findingId}`, payload);
              setReview(response.data.review);
              setNotes((prev) => ({ ...prev, [_finding.findingId]: payload.action }));
              setSessionNote("");
              if (payload.action === "correct_context" || payload.action === "save_guide") {
                setBrainNote("Future reviews will use this decision.");
              }
            }}
          />
        </div>
      ))}
      {experimental && mutateAllowed && !running ? (
        <form
          className="rr-stack"
          onSubmit={(event) => {
            event.preventDefault();
            createTask();
          }}
        >
          <label>
            Desired outcome
            <textarea value={outcome} onChange={(event) => setOutcome(event.target.value)} rows={3} />
          </label>
          <p>{DRAFT_TASK_NOTE}</p>
          {stale ? (
            <label>
              <input type="checkbox" checked={confirmStale} onChange={(event) => setConfirmStale(event.target.checked)} />
              This review is stale. Create the draft from this commit anyway.
            </label>
          ) : null}
          <div className="rr-actions">
            <button type="submit" className="rr-button" disabled={!Object.values(selected).some(Boolean) || (stale && !confirmStale)}>
              {CREATE_TASK_LABEL}
            </button>
            <Link to="/">Done</Link>
            <Link to={`/brain/new?repository=${review.repoId || ""}&title=${encodeURIComponent((review.findings || [])[0]?.title || "")}&body=${encodeURIComponent((review.findings || [])[0]?.productImpact || "")}`}>
              Add context
            </Link>
          </div>
        </form>
      ) : null}
      {relatedTasks.length ? (
        <p>
          Tasks from this review:{" "}
          {relatedTasks.map((task) => (
            <Link key={task.id} to={`/tasks/${task.id}`}>{task.title || "Task"}</Link>
          ))}
        </p>
      ) : null}
      {draft ? <CorrectionPlanPreview review={review} finding={draft} onClose={() => setDraft(null)} /> : null}
      </div>
      <aside className="rr-review-rail" aria-label="Review context">
        <section>
          <h2>Pull request</h2>
          <p><a href={review.pullRequestUrl}>{review.owner}/{review.name} #{review.pullNumber}</a></p>
          <p><CopyableSha sha={review.headSha} /></p>
          <p>{stale ? "Stale" : "Current"}{review.completedAt ? ` · ${formatWhenSafe(review.completedAt)}` : ""}</p>
        </section>
        {review.status !== "failed" ? <ReviewPublicationStatus review={review} capability={capability} canRetry={mutateAllowed} onRetry={retry} onReauthorize={mutateAllowed ? reauthorize : null} /> : null}
        <section aria-label="Company Brain used in this review">
          <h2>Company Brain used</h2>
          {(review.contextSnapshot?.guides || []).length ? (
            <ul className="rr-notes">
              {review.contextSnapshot.guides.map((guide) => (
                <li key={`${guide.guideId}-${guide.version}`}>
                  <Link to={`/brain/${guide.guideId}?repository=${review.repoId || ""}`}>{guide.title}</Link>
                  <span>v{guide.version}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p>No Company Brain context was stored with this review. The review still used the pull request.</p>
          )}
        </section>
      </aside>
    </article>
  );
}

function formatWhenSafe(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}
